import { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "antd";
import type { FormInstance } from "antd";
import apiClient from "../api/apiClient";
import {
  getSuggestedTreasuryAccountId,
  type CommercialPaymentRoute,
} from "../utils/commercialTreasurySuggestion";
export { getSuggestedTreasuryAccountId } from "../utils/commercialTreasurySuggestion";

type AccountOption = { id: number | string };
type FieldName = string | Array<string | number>;

export const usePaymentRoutes = (
  warehouseId: unknown,
  enabled = true,
  moduleScope: "commercial" | "logistics" = "commercial",
  companyId?: unknown,
) => {
  const [routeResult, setRouteResult] = useState<{
    context: string;
    routes: CommercialPaymentRoute[];
    loaded: boolean;
  } | null>(null);
  const warehouseValue =
    warehouseId && typeof warehouseId === "object" && "id" in warehouseId
      ? (warehouseId as { id?: unknown }).id
      : warehouseId;
  const normalizedWarehouseId = Number(warehouseValue);
  const context = `${companyId ?? ""}:${normalizedWarehouseId}:${moduleScope}`;

  useEffect(() => {
    if (!enabled || !Number.isInteger(normalizedWarehouseId) || normalizedWarehouseId <= 0 ||
        (moduleScope === "logistics" && !(Number(companyId) > 0))) {
      setRouteResult(null);
      return;
    }

    const controller = new AbortController();
    setRouteResult(null);
    apiClient.get(
      `/api/warehouses/${normalizedWarehouseId}/${moduleScope === "commercial" ? "commercial-payment-route-suggestions" : "payment-route-suggestions"}`,
      { signal: controller.signal, params: { company_id: companyId, module_scope: moduleScope } },
    ).then((response) => {
      if (controller.signal.aborted) return;
      setRouteResult({
        context,
        routes: Array.isArray(response.data?.data) ? response.data.data : [],
        loaded: response.data?.success === true,
      });
    }).catch(() => {
      if (!controller.signal.aborted) {
        setRouteResult(null);
      }
    });

    return () => { controller.abort(); };
  }, [enabled, normalizedWarehouseId, moduleScope, companyId, context]);

  const currentResult = enabled && routeResult?.context === context
    ? routeResult
    : null;
  return { routes: currentResult?.routes || [], loaded: currentResult?.loaded || false };
};

export const useCommercialPaymentRoutes = (warehouseId: unknown, enabled = true) =>
  usePaymentRoutes(warehouseId, enabled);

export const useTreasuryAccountSuggestion = (
  form: FormInstance,
  {
    warehouseId,
    enabled,
    modeField,
    accountField,
    accounts,
    moduleScope = "commercial",
    companyId,
    preserveManualChoice = moduleScope === "logistics",
    referenceCurrencyOnly = false,
  }: {
    warehouseId: unknown;
    enabled: boolean;
    modeField: FieldName;
    accountField: FieldName;
    accounts: AccountOption[];
    moduleScope?: "commercial" | "logistics";
    companyId?: unknown;
    preserveManualChoice?: boolean;
    referenceCurrencyOnly?: boolean;
  },
) => {
  const { routes: configuredRoutes, loaded } = usePaymentRoutes(warehouseId, enabled, moduleScope, companyId);
  const routes = useMemo(() => referenceCurrencyOnly ? configuredRoutes.filter((route) =>
    Number(route.company_currency_id) > 0 && Number(route.currency_id) === Number(route.company_currency_id)) : configuredRoutes,
  [configuredRoutes, referenceCurrencyOnly]);
  const selectedModeId = Form.useWatch(modeField, form);
  const automaticAccount = useRef<number>();
  useEffect(() => { automaticAccount.current = undefined; }, [companyId, warehouseId, moduleScope, enabled]);

  useEffect(() => {
    if (!enabled || !loaded || !selectedModeId || !accounts.length) return;
    const selectedAccountId = form.getFieldValue(accountField);
    if (selectedAccountId && accounts.some(
      (account) => Number(account.id) === Number(selectedAccountId),
    ) && (!preserveManualChoice || Number(selectedAccountId) !== automaticAccount.current)) return;
    const accountId = getSuggestedTreasuryAccountId(selectedModeId, routes, accounts);
    if (accountId || selectedAccountId) {
      automaticAccount.current = accountId;
      form.setFieldValue(accountField, accountId);
    }
  }, [accountField, accounts, enabled, form, loaded, routes, selectedModeId, preserveManualChoice]);

  const onModeChange = (paymentModeId: unknown) => {
    const current = form.getFieldValue(accountField);
    if (preserveManualChoice && current && Number(current) !== automaticAccount.current &&
        accounts.some((account) => Number(account.id) === Number(current))) return;
    const accountId = loaded
      ? getSuggestedTreasuryAccountId(paymentModeId, routes, accounts)
      : undefined;
    automaticAccount.current = accountId;
    form.setFieldValue(accountField, accountId);
  };

  return { onModeChange, onAccountChange: () => { automaticAccount.current = undefined; } };
};

export const useCommercialTreasuryAccountSuggestion = (
  form: FormInstance,
  options: Parameters<typeof useTreasuryAccountSuggestion>[1],
) => useTreasuryAccountSuggestion(form, { ...options, moduleScope: "commercial" });
